/**
 * Maps filtered sibling orders from the data accumulator into clubbed table rows.
 * @module expandableTableForOrderAuth/hooks/useClubbedDataFromFilteredSiblingOrders
 */
import { useMemo } from "react";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { SiblingOrder } from "../../../data-model/siblingOrdersModel";
import { SingleOrderClubbedData } from "../types";

/**
 * Maps raw sibling orders into {@link SingleOrderClubbedData} rows for
 * {@link ExpandableTableForOrderAuth}.
 *
 * @param siblings - Sibling orders from the filtered or raw sibling-orders
 *   response; non-array values yield an empty list
 * @returns Clubbed rows with `order_data` and `auth_on_file_data` field names
 */
export const mapSiblingOrdersToClubbedData = (
  siblings: SiblingOrder[] | undefined,
): SingleOrderClubbedData[] => {
  if (!Array.isArray(siblings)) {
    return [];
  }

  return siblings.map((sibling) => ({
    order_id: sibling.order_id,
    order_data: sibling.order,
    auth_on_file_data: sibling.auth_on_file,
  }));
};

/**
 * Reads filtered sibling orders from the data accumulator context and returns
 * them as clubbed rows for {@link ExpandableTableForOrderAuth}.
 *
 * @returns Memoized {@link SingleOrderClubbedData} rows derived from
 *   `filteredSiblingOrders.siblings`
 */
export const useClubbedDataFromFilteredSiblingOrders =
  (): SingleOrderClubbedData[] => {
    const { filteredSiblingOrders } = useDataAccumulatorForSingleOrder();

    return useMemo(
      () => mapSiblingOrdersToClubbedData(filteredSiblingOrders?.siblings),
      [filteredSiblingOrders?.siblings],
    );
  };
